<script>
    import { Filemanager, Willow } from 'wx-svelte-filemanager';
    import { Locale } from '@svar-ui/svelte-core';
    import { en } from '@svar-ui/filemanager-locales';

    let { data = [] } = $props();

    // Auf Mobile (Einstiegs-Ebene zeigt nur Folder, keine Files) startet die
    // Ansicht als Liste. User kann jederzeit auf Kacheln umschalten.
    const initialMode = typeof window !== 'undefined' && window.innerWidth < 768
        ? 'table'
        : 'cards';

    // Deutsche Übersetzung – SVAR liefert keine de.js mit, wir mergen über en.
    const locale = {
        ...en,
        filemanager: {
            ...en.filemanager,
            'My files': 'Intranet',
            Search: 'Suche',
            'Search results in': 'Suchergebnisse in',
            Result: 'Ergebnis',
            Found: 'Gefunden',
            Information: 'Informationen',
            Ok: 'OK',
            Cancel: 'Abbrechen',
            'Back to parent folder': 'Zurück zum übergeordneten Ordner',
            Files: 'Dateien',
            Folder: 'Ordner',
            Size: 'Größe',
            Name: 'Name',
            Type: 'Typ',
            Date: 'Datum',
            Download: 'Öffnen',
            Preview: 'Vorschau',
            Count: 'Anzahl',
            folder: 'Ordner',
            folders: 'Ordner',
            file: 'Datei',
            files: 'Dateien',
            'Multiple files': 'Mehrere Dateien',
            multiple: 'mehrere',
            of: 'von',
            used: 'belegt',
            'Select file or folder to view details':
                'Datei oder Ordner auswählen, um Details zu sehen',
            'Looks like nothing is here': 'Hier ist nichts',
            'Unknown file': 'Unbekannte Datei',
            'A miniature file preview': 'Miniatur-Vorschau',
        },
    };

    /**
     * Custom Context-Menu: nur "Öffnen" für Files, sonst nichts.
     */
    function customMenu(mode) {
        if (mode === 'file') {
            return [{
                id: 'download',
                icon: 'wxi-eye',
                text: 'Öffnen',
                hotkey: '',
            }];
        }
        return [];
    }

    /**
     * Icon-Callback: Thumbnail wenn vorhanden, sonst SVAR-CDN-Icon nach Extension.
     */
    const SVAR_ICON_EXTENSIONS = new Set([
        '7z', 'rar', 'zip',
        'css', 'html', 'js', 'php', 'md', 'xml', 'sql',
        'aif', 'mid', 'mp3', 'waw', 'wma',
        'doc', 'docx', 'txt',
        'avi', 'mov', 'mp4', 'mpeg', 'mpg',
        'pdf',
        'xls', 'xlsx',
        'gif', 'jpg', 'jpeg', 'png', 'psd', 'tiff', 'svg',
    ]);

    function customIcons(file, size) {
        // icons() ist die Fallback-Variante (klein 100×100). Für Files mit
        // Thumbnail nutzen wir aber besser previews() (full-width 154px),
        // siehe unten — daher hier kein Thumbnail-Return, sondern direkt SVAR-Icon.
        if (file?.type === 'folder') return false;
        // realExt kommt vom PHP-Backend (wir haben die Extension aus dem
        // Display-Namen rausgenommen, daher ist SVAR's `ext` leer und wir
        // nutzen unser eigenes Feld für die Type-Bestimmung).
        const ext = (file?.realExt || file?.ext || '').toLowerCase();
        const icon = SVAR_ICON_EXTENSIONS.has(ext) ? ext : 'file';
        return `https://cdn.svar.dev/icons/filemanager/vivid/${size}/${icon}.svg`;
    }

    /**
     * Preview-Callback: für Files mit `thumbnail`-Feld (PDFs/Bilder) wird hier
     * unsere FAL-generierte Thumbnail-URL zurückgegeben. SVAR rendert das dann
     * full-width im Card-Preview-Bereich (154px hoch, 100% breit) statt als
     * winziges 100×100-Icon. Bessere Erkennbarkeit, vor allem auf Mobile.
     */
    function customPreviews(file) {
        return file?.thumbnail || null;
    }

    function init(api) {
        // Sicherheitsnetz: wenn ein Thumbnail-<img> nicht lädt (PDF das gm nicht
        // rendern kann o.ä.), auf ein generisches Datei-Icon zurückfallen statt
        // den "Miniatur-Vorschau"-Alt-Text anzuzeigen. error bubbelt nicht →
        // Capture-Phase auf dem Mount-Root.
        const mountRoot = document.getElementById('intranet-filebrowser');
        if (mountRoot) {
            mountRoot.addEventListener(
                'error',
                (e) => {
                    const img = e.target;
                    if (
                        img &&
                        img.tagName === 'IMG' &&
                        img.classList?.contains('wx-card-preview') &&
                        !img.dataset.intranetFallback
                    ) {
                        img.dataset.intranetFallback = '1';
                        img.style.objectFit = 'contain';
                        img.src = 'https://cdn.svar.dev/icons/filemanager/vivid/big/file.svg';
                    }
                },
                true
            );
        }

        // DocuWare-Quelldokument-Link: SVAR hat keine Custom-Column-API – die
        // Tabellen-Spalten (Name/Größe/Datum) sind in der Komponente hart
        // codiert. Wir injizieren daher pro Zeile einen echten <a target=_blank>
        // mit der vom PHP-Backend gelieferten `dwUrl`. Sichtbarkeit (nur Desktop
        // ≥1920px) regelt das CSS unten. Ein MutationObserver re-injiziert nach
        // Ordner-Wechsel, View-Switch und Scroll (Grid-Virtualisierung).
        const DW_LINK_CLASS = 'intranet-dw-link';
        // DocuWare-Logo als Inline-SVG (blauer Kreis, weißes pixeliges "D").
        // Bewusst inline statt CDN/Datei: self-contained, scharf in jeder Größe
        // und ohne Netz-Abhängigkeit (Intranet ist LAN-only).
        const DW_ICON =
            '<svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true" ' +
            'xmlns="http://www.w3.org/2000/svg">' +
            '<circle cx="24" cy="24" r="24" fill="#3b3bc6"/>' +
            '<g fill="#ffffff">' +
            '<path d="M23 9 A15 15 0 0 1 23 39 L23 32.5 A8.5 8.5 0 0 0 23 15.5 Z"/>' +
            '<rect x="16.6" y="15.5" width="6.4" height="6.4"/>' +
            '<rect x="10.2" y="21.9" width="6.4" height="6.4"/>' +
            '<rect x="16.6" y="28.3" width="6.4" height="6.4"/>' +
            '</g></svg>';

        const injectDwLinks = () => {
            if (!mountRoot) return;
            // Header-Label "DW-Link" (kosmetisch, passend zum Screenshot)
            const header = mountRoot.querySelector('.wx-header');
            if (header && !header.querySelector(':scope > .intranet-dw-header')) {
                const lbl = document.createElement('span');
                lbl.className = 'intranet-dw-header';
                lbl.textContent = 'DW-Link';
                header.appendChild(lbl);
            }
            const rows = mountRoot.querySelectorAll('.wx-row[data-id]');
            rows.forEach((row) => {
                if (row.querySelector(':scope > .' + DW_LINK_CLASS)) return;
                // setID() prefixt String-IDs mit ":" – wieder abschneiden
                const rawId = row.getAttribute('data-id') || '';
                const id = rawId.startsWith(':') ? rawId.slice(1) : rawId;
                if (!id || id === '/wx-filemanager-parent-link') return;
                const file = api.getFile(id);
                if (!file?.dwUrl) return;
                const a = document.createElement('a');
                a.className = DW_LINK_CLASS;
                a.href = file.dwUrl;
                a.target = '_blank';
                a.rel = 'noopener';
                a.title = 'Quelldokument im DocuWare-Archiv öffnen';
                a.setAttribute('aria-label', a.title);
                a.innerHTML = DW_ICON;
                // Klick darf NICHT die Zeile öffnen – SVAR delegiert Klicks in
                // der Bubble-Phase über die .wx-list/.wx-data-Container. Browser
                // öffnet den Link via href/target selbst.
                a.addEventListener('click', (e) => e.stopPropagation());
                row.appendChild(a);
            });
            // Header-Label nur zeigen, wenn die aktuelle Ansicht überhaupt einen
            // DW-Link enthält – in reinen Ordner-Ebenen sonst eine leere Spalte.
            const headerLabel = mountRoot.querySelector('.intranet-dw-header');
            if (headerLabel) {
                const hasLink = mountRoot.querySelector('.wx-row .' + DW_LINK_CLASS) !== null;
                headerLabel.classList.toggle('is-empty', !hasLink);
            }
        };

        if (mountRoot) {
            const dwObserver = new MutationObserver(() => injectDwLinks());
            dwObserver.observe(mountRoot, { childList: true, subtree: true });
            injectDwLinks();
        }

        const openInTab = (config) => {
            const file = api.getFile(config.id);
            if (file?.url) {
                window.open(file.url, '_blank', 'noopener');
            }
            return false;
        };
        api.intercept('download-file', openInTab);
        api.intercept('open-file', openInTab);

        // Drag & Drop von externen Dateien komplett unterbinden.
        // SVAR's `readonly`-Prop deaktiviert zwar die Edit-Menüs, hält die
        // interne UploadDropArea aber visuell aktiv – User können Dateien
        // reinziehen, es entsteht kurz eine Kachel, verschwindet wieder
        // (weil kein Upload-Backend). Verwirrende UX.
        //
        // Zwei Schutzschichten:
        //  1. DOM-Ebene: Drag-Events auf dem Mount-Root abfangen (Capture),
        //     bevor SVAR sie sieht. Kein preventDefault auf dragover →
        //     Browser-Default (drop verweigern) greift, User sieht das
        //     "verboten"-Cursor-Icon. drop wird zusätzlich preventDefault'd
        //     damit der Browser die Datei nicht selbst öffnet.
        //  2. Store-Ebene: create-file-Action zurückweisen, falls sich doch
        //     mal etwas durchmogelt.
        if (mountRoot) {
            ['dragenter', 'dragleave'].forEach((evt) =>
                mountRoot.addEventListener(evt, (e) => e.stopPropagation(), true)
            );
            mountRoot.addEventListener(
                'dragover',
                (e) => e.stopPropagation(),
                true
            );
            mountRoot.addEventListener(
                'drop',
                (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                },
                true
            );
        }
        api.intercept('create-file', () => false);

        // Single-Klick/Tap soll überall navigieren bzw. öffnen – kein Selektieren-
        // dann-Doppelklick-Workflow. Wir mappen alle relevanten Klick-Pfade auf
        // eine Aktion direkt:
        //   - Back-Link "Zurück zum übergeordneten Ordner" (SVAR feuert
        //     select-file mit type:'navigation' und ohne id) → eine Ebene hoch
        //   - Folder → set-path (rein in den Folder)
        //   - File   → window.open (URL im neuen Tab)
        // toggle=true filtern wir aus, weil das von Breadcrumb-Klicks getriggert
        // wird (set-path → select-file mit toggle=true für vorherige Auswahl)
        // und sonst zu einer Endlos-Navigation zurück führen würde.
        api.on('select-file', (config) => {
            if (config?.type === 'navigation') {
                const state = api.getState();
                const panelIdx = state.activePanel;
                const crumbs = state.panels?.[panelIdx]?._crumbs || [];
                if (crumbs.length > 1) {
                    api.exec('set-path', {
                        id: crumbs[crumbs.length - 2].id,
                        panel: panelIdx,
                    });
                }
                return;
            }
            if (!config?.id) return;
            if (config.toggle) return;
            const file = api.getFile(config.id);
            if (file?.type === 'folder') {
                api.exec('set-path', { id: config.id, panel: config.panel });
            } else if (file?.url) {
                window.open(file.url, '_blank', 'noopener');
            }
        });

        // Mobile: nach Folder-Auswahl im Tree (Sidebar) die Tree-View wieder
        // schließen. SVAR macht das nicht automatisch, weil der Klick INNERHALB
        // der Sidebar war. Wir klicken den Toggle-Tree-Button programmatisch
        // wenn die Sidebar gerade als Overlay sichtbar ist.
        api.on('set-path', () => {
            if (window.innerWidth >= 768) return;
            const sidebarVisible = document.querySelector(
                '.intranet-filemanager .wx-sidebar.wx-sidebar-visible'
            );
            if (!sidebarVisible) return;
            const toggleBtn = document.querySelector(
                '.intranet-filemanager [data-id=":toggle-tree"]'
            );
            toggleBtn?.click();
        });

        // narrowMode (mobile) zeigt für Folder/File ein "Vorschau"-Context-Menu
        // statt direkt zu navigieren. Wir übernehmen die show-preview-Action und
        // routen sie auf Folder-Navigation bzw. File-Öffnen um.
        api.intercept('show-preview', () => {
            const state = api.getState();
            const panelIdx = state.activePanel;
            const sel = state.panels?.[panelIdx]?.selected || [];
            if (sel.length === 1) {
                const file = api.getFile(sel[0]);
                if (file?.type === 'folder') {
                    api.exec('set-path', { id: file.id, panel: panelIdx });
                } else if (file?.url) {
                    window.open(file.url, '_blank', 'noopener');
                }
            }
            return false;
        });
    }
</script>

<div class="intranet-filemanager">
    <Locale words={locale}>
        <Willow>
            <Filemanager
                {data}
                {init}
                readonly={true}
                mode={initialMode}
                menuOptions={customMenu}
                icons={customIcons}
                previews={customPreviews}
            />
        </Willow>
    </Locale>
</div>

<style>
    .intranet-filemanager {
        width: 100%;
        height: 100%;
        min-height: 500px;
    }

    /* Refresh-Button in Breadcrumbs ausblenden – Cache ist eh deaktiviert,
       Refresh wäre nur verwirrend für den User */
    .intranet-filemanager :global(.wx-refresh-icon) {
        display: none !important;
    }

    /* Eye-Icon (Preview-Pane-Toggle) – wir wollen keinen Side-Pane */
    .intranet-filemanager :global(.wx-preview-icon) {
        display: none !important;
    }

    /* 3-Punkte-Menu auf den Cards – Klick auf File öffnet eh direkt, das
       Dropdown ist redundant */
    .intranet-filemanager :global(.wx-more) {
        display: none !important;
    }

    /* Thumbnails: oberen Ausschnitt im echten Seitenverhältnis zeigen statt
       das Bild zu stauchen. Bei A4-Dokumenten ist oben meistens der Titel /
       erste Inhalte – damit ist die Vorschau aussagekräftig. */
    .intranet-filemanager :global(.wx-file-preview .wx-card-preview) {
        object-fit: cover !important;
        object-position: top !important;
    }

    /* Card-Titel ÜBER dem Thumbnail, Gemeinhardt-Brand-Blau, fett.
       Feste Kachelhöhe für ein ruhiges, gleichmäßiges Raster; Titel bricht
       auf bis zu 3 Zeilen um (danach …), Thumbnail-Bereich fix 154px. */
    .intranet-filemanager :global(.wx-cards .wx-item) {
        height: 232px !important;
    }
    .intranet-filemanager :global(.wx-cards .wx-info) {
        order: -1;
        height: 76px !important;
        align-items: flex-start !important;
        padding: 10px 12px !important;
        border-bottom: 2px solid #0D54A1;
        background: #fff;
        box-sizing: border-box;
        overflow: hidden;
    }
    .intranet-filemanager :global(.wx-cards .wx-file-name),
    .intranet-filemanager :global(.wx-cards .wx-folder-name) {
        max-width: 100% !important;
        overflow: hidden !important;
    }
    .intranet-filemanager :global(.wx-cards .wx-name) {
        color: #0D54A1 !important;
        font-weight: 600 !important;
        display: -webkit-box !important;
        -webkit-line-clamp: 3 !important;
        -webkit-box-orient: vertical !important;
        overflow: hidden !important;
        white-space: normal !important;
        /* lange Wörter ohne Leerzeichen umbrechen statt überlaufen */
        overflow-wrap: anywhere !important;
        word-break: break-word !important;
        line-height: 1.3;
    }
    /* Thumbnail-Bereich fix bei 154px halten. */
    .intranet-filemanager :global(.wx-cards .wx-preview) {
        flex: 0 0 154px !important;
        height: 154px !important;
    }

    /* "Vorschau"-Item im Context-Menu (narrowMode/Mobile) – wir routen alle
       Klicks direkt um (Folder navigiert, File öffnet), das Menu-Item ist
       nur störend. data-id=":preview" wegen SVAR's setID()-Prefix. */
    :global(.wx-menu .wx-option[data-id=":preview"]) {
        display: none !important;
    }
    /* Wenn das Vorschau-Item das einzige war, restliches leeres Menu auch weg.
       :empty greift hier nicht (display:none zählt noch als Child), darum
       auch das Menu blank machen wenn nur preview drin war. */
    :global(.wx-menu:has(> .wx-option[data-id=":preview"]:only-child)) {
        display: none !important;
    }

    /* Split-View (Panels-Mode, 3. Button im Switcher) ausblenden –
       für Read-only-Filebrowser kein sinnvoller Use-Case.
       :nth-of-type ist verlässlicher als :nth-child (Svelte fügt
       Comment-Nodes zwischen Buttons ein). */
    .intranet-filemanager :global(.wx-segmented button:nth-of-type(3)) {
        display: none !important;
    }

    /* Listenansicht: Dateiname mehrzeilig statt abschneiden. Rows haben per
       Default fix 42px Höhe (inline für die Virtualisierung) – wir erlauben
       auto-Höhe + Umbruch. Bei sehr großen Ordnern mit Scroll kann die
       Scroll-Position minimal driften, für typische Ordnergrößen unkritisch. */
    .intranet-filemanager :global(.wx-row) {
        height: auto !important;
        min-height: 42px;
    }
    .intranet-filemanager :global(.wx-row [data-col-id=":name"]) {
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
        word-break: break-word;
        line-height: 1.3;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
        align-items: center;
    }
    .intranet-filemanager :global(.wx-row [data-col-id=":name"] .wx-name) {
        white-space: normal !important;
        overflow: visible !important;
    }

    /* "Zurück zum übergeordneten Ordner" als auffällige Tile – auf ALLEN
       Viewports (Desktop war vorher der kleine SVAR-Default-Textlink).
       Blaue Umrandung + heller Brand-Hintergrund + fett. */
    .intranet-filemanager :global(.wx-back-item) {
        width: 100%;
        /* symmetrischer Abstand oben wie unten – SVAR setzt bei vorhandenem
           Back-Link das Top-Padding des Card-Containers sonst auf 0 */
        margin: 16px 0 16px 0 !important;
        font-size: 15px !important;
        line-height: 1.4 !important;
    }
    .intranet-filemanager :global(.wx-back) {
        width: 100% !important;
        box-sizing: border-box !important;
        min-height: 48px;
        padding: 12px 16px !important;
        background: #eef3fa !important;
        border: 1px solid #0D54A1 !important;
        border-radius: 6px !important;
        font-weight: 600 !important;
        align-items: center;
    }
    .intranet-filemanager :global(.wx-back i) {
        font-size: 22px !important;
        margin-right: 12px !important;
    }

    /* Sidebar (Tree) auf Desktop deutlich breiter (Default war 238px) */
    .intranet-filemanager :global(.wx-sidebar:not(.wx-sidebar-narrow)) {
        flex: 0 0 320px !important;
        min-width: 320px !important;
        width: 320px !important;
    }

    /* Datums- und Größen-Spalten in der Tabellen-Ansicht ausblenden.
       SVAR's setID() prependiert ":" vor String-IDs, daher lautet das
       Attribut tatsächlich data-col-id=":size" (nicht "size"). */
    .intranet-filemanager :global([data-col-id=":size"]),
    .intranet-filemanager :global([data-col-id=":date"]) {
        display: none !important;
    }
    /* Zugehörige Header-Cells (Position 2 + 3 im Header-Row, nth-of-type für
       Robustheit gegen Svelte-Comment-Nodes) */
    .intranet-filemanager :global(.wx-header .wx-cell:nth-of-type(2)),
    .intranet-filemanager :global(.wx-header .wx-cell:nth-of-type(3)) {
        display: none !important;
    }
    /* Card-Variante: Footer mit Datum/Größe ausblenden */
    .intranet-filemanager :global(.wx-card .wx-info),
    .intranet-filemanager :global(.wx-card-footer) {
        display: none !important;
    }

    /* Mobile: größere Tap-Targets in der Tabelle */
    @media (max-width: 768px) {
        .intranet-filemanager :global(.wx-row),
        .intranet-filemanager :global(.wx-each-row) {
            min-height: 56px !important;
        }

        /* Cards-View: eine Karte pro Reihe, damit der Dateititel nicht
           "Sicherheitsdatenblatt..." abgeschnitten wird. Höhe bewusst auf
           den SVAR-Default belassen — die manuellen Mobile-Höhen
           (280px Card / 220px Preview) haben das Aspect-Ratio verzerrt.
           Wichtig: .wx-item wird AUCH in den Breadcrumbs verwendet – daher
           hier auf .wx-cards .wx-item scopen, sonst zerschießt's das
           Breadcrumb-Layout. */
        .intranet-filemanager :global(.wx-cards .wx-item) {
            width: 100% !important;
            margin-right: 0 !important;
        }
        /* Titel-Container darf volle Breite nutzen (Default war 80% bzw. 160px) */
        .intranet-filemanager :global(.wx-folder-name),
        .intranet-filemanager :global(.wx-file-name) {
            max-width: 100% !important;
        }

        /* Back-Link auf Mobile minimal höher (Basis-Style gilt sonst überall) */
        .intranet-filemanager :global(.wx-back) {
            min-height: 52px;
            padding: 14px 16px !important;
        }
    }

    /* DocuWare-Quelldokument-Link: standardmäßig ausgeblendet ("Desktop-Ansicht"
       laut Roland = nicht mobil). Erst ab 1920px Viewport-Breite sichtbar –
       deckt den Mobile/Tablet-Ausschluss automatisch mit ab. Der Link selbst
       wird per JS (init/MutationObserver) in jede Tabellenzeile injiziert. */
    .intranet-filemanager :global(.intranet-dw-link),
    .intranet-filemanager :global(.intranet-dw-header) {
        display: none;
    }

    @media (min-width: 1920px) {
        /* Zeile als Positionierungs-Kontext für den absolut platzierten Link */
        .intranet-filemanager :global(.wx-row) {
            position: relative;
        }
        .intranet-filemanager :global(.intranet-dw-link) {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            right: 12px;
            top: 0;
            bottom: 0;
            margin: auto 0;
            height: 28px;
            width: 28px;
            color: #0D54A1;
            text-decoration: none;
            z-index: 2;
        }
        .intranet-filemanager :global(.intranet-dw-link:hover) {
            opacity: 0.65;
        }
        /* Rechts Platz reservieren, damit lange Dateinamen nicht unter das
           Icon laufen */
        .intranet-filemanager :global(.wx-row [data-col-id=":name"]) {
            padding-right: 48px !important;
        }
        /* Header-Label rechtsbündig passend über der Link-Spalte */
        .intranet-filemanager :global(.wx-header) {
            position: relative;
        }
        .intranet-filemanager :global(.intranet-dw-header) {
            display: inline-flex;
            align-items: center;
            position: absolute;
            right: 8px;
            top: 0;
            bottom: 0;
            margin: auto 0;
            font-weight: 600;
            color: #1f2937;
            pointer-events: none;
        }
    }

    /* Reine Ordner-Ebene (kein File mit DW-Link): Spaltenkopf ausblenden.
       Höhere Spezifität als die Media-Query-Regel, gewinnt also auch >=1920px. */
    .intranet-filemanager :global(.intranet-dw-header.is-empty) {
        display: none !important;
    }
</style>
